import { Modal } from 'bootstrap'
import type { Expediente } from '../expedientes/cliente-expedientes.js'
import { ErrorApi } from '../identidad/cliente-identidad.js'
import { cerrarCarga, mostrarCarga, notificar } from '../fundacion/alertas.js'
import {
  actualizarUnidad,
  actualizarPlaneacion,
  crearPlaneacion,
  crearUnidad,
  listarPlaneaciones,
  listarUnidades,
  type DatosPlaneacion,
  type PlaneacionClase,
  type UnidadDidactica,
} from './cliente-planeacion.js'

const elemento = <T extends Element>(selector: string): T => {
  const dato = document.querySelector<T>(selector)
  if (!dato) throw new Error(`No existe ${selector}`)
  return dato
}
const textoError = (error: unknown) =>
  error instanceof ErrorApi ? error.message : 'No fue posible completar la operación.'
let expediente: Expediente | null = null
let roles: string[] = []
let unidades: UnidadDidactica[] = []
let unidadActual: UnidadDidactica | null = null
let planeacionEditada: PlaneacionClase | null = null
let unidadEditada: UnidadDidactica | null = null
const modalUnidad = new Modal(elemento('#modal-editar-unidad'))
const modalVerPlaneacion = new Modal(elemento('#modal-ver-planeacion'))

function informar(mensaje: string, tipo = 'success') {
  const alerta = elemento<HTMLElement>('#alerta-planeacion')
  alerta.className = 'alert d-none'
  notificar(mensaje, tipo === 'danger' ? 'error' : (tipo as 'success' | 'warning' | 'info'))
}
const puedeEditar = () => roles.includes('ESTUDIANTE') && expediente?.estado === 'ABIERTO'
const texto = (selector: string) => elemento<HTMLTextAreaElement>(selector).value
function filasCompetencias(): Array<{
  competencia: string
  componente: string
  indicadorDesempeno: string
}> {
  return Array.from(
    document.querySelectorAll<HTMLElement>('#tabla-competencias-planeacion tr'),
  ).map((fila) => ({
    competencia:
      fila.querySelector<HTMLInputElement>('[data-campo="competencia"]')?.value.trim() ?? '',
    componente:
      fila.querySelector<HTMLInputElement>('[data-campo="componente"]')?.value.trim() ?? '',
    indicadorDesempeno:
      fila.querySelector<HTMLInputElement>('[data-campo="indicador"]')?.value.trim() ?? '',
  }))
}
const datosFormulario = (): DatosPlaneacion => ({
  secuencia: Number(elemento<HTMLInputElement>('#secuencia-planeacion').value),
  tema: elemento<HTMLInputElement>('#tema-planeacion').value.trim(),
  competencias: filasCompetencias(),
  referenteConceptual: texto('#referente-planeacion'),
  momentoInicial: texto('#inicial-planeacion'),
  momentoCentral: texto('#central-planeacion'),
  momentoFinal: texto('#final-planeacion'),
  metodologia: texto('#metodologia-planeacion'),
  evaluacion: texto('#evaluacion-planeacion'),
  recursos: texto('#recursos-planeacion'),
  bibliografia: texto('#bibliografia-planeacion'),
  observaciones: texto('#observaciones-planeacion'),
})

function cargarFormulario(dato?: PlaneacionClase): void {
  planeacionEditada = dato ?? null
  elemento<HTMLInputElement>('#secuencia-planeacion').value = String(dato?.secuencia ?? '')
  elemento<HTMLInputElement>('#tema-planeacion').value = dato?.tema ?? ''
  renderizarFilasCompetencias(
    dato?.competencias ?? [{ competencia: '', componente: '', indicadorDesempeno: '' }],
  )
  for (const [selector, valor] of [
    ['#referente-planeacion', dato?.referenteConceptual],
    ['#inicial-planeacion', dato?.momentoInicial],
    ['#central-planeacion', dato?.momentoCentral],
    ['#final-planeacion', dato?.momentoFinal],
    ['#metodologia-planeacion', dato?.metodologia],
    ['#evaluacion-planeacion', dato?.evaluacion],
    ['#recursos-planeacion', dato?.recursos],
    ['#bibliografia-planeacion', dato?.bibliografia],
    ['#observaciones-planeacion', dato?.observaciones],
  ] as const) {
    elemento<HTMLTextAreaElement>(selector).value = valor ?? ''
  }
  elemento<HTMLButtonElement>('#guardar-planeacion').textContent = dato
    ? 'Guardar cambios'
    : 'Agregar planeación'
  elemento<HTMLButtonElement>('#cancelar-edicion-planeacion').classList.toggle('d-none', !dato)
}

function renderizarFilasCompetencias(
  datos: Array<{ competencia: string; componente: string; indicadorDesempeno: string }>,
): void {
  const cuerpo = elemento<HTMLTableSectionElement>('#tabla-competencias-planeacion')
  cuerpo.replaceChildren(
    ...datos.map((dato, indice) => {
      const fila = document.createElement('tr')
      for (const [clave, valor, etiqueta] of [
        ['competencia', dato.competencia, 'Competencia'],
        ['componente', dato.componente, 'Componente'],
        ['indicador', dato.indicadorDesempeno, 'Indicador de desempeño'],
      ] as const) {
        const celda = document.createElement('td')
        const campo = document.createElement('input')
        campo.type = 'text'
        campo.className = 'form-control'
        campo.maxLength = clave === 'indicador' ? 500 : 255
        campo.required = true
        campo.value = valor
        campo.dataset.campo = clave
        campo.ariaLabel = `${etiqueta} ${indice + 1}`
        celda.append(campo)
        fila.append(celda)
      }
      const acciones = document.createElement('td')
      const quitar = document.createElement('button')
      quitar.type = 'button'
      quitar.className = 'btn btn-sm btn-outline-danger'
      quitar.textContent = 'Quitar'
      quitar.disabled = datos.length === 1
      quitar.addEventListener('click', () => {
        fila.remove()
        actualizarBotonesCompetencias()
      })
      acciones.append(quitar)
      fila.append(acciones)
      return fila
    }),
  )
}

function actualizarBotonesCompetencias(): void {
  const filas = Array.from(
    document.querySelectorAll<HTMLTableRowElement>('#tabla-competencias-planeacion tr'),
  )
  filas.forEach((fila) => {
    const boton = fila.querySelector<HTMLButtonElement>('button')
    if (boton) boton.disabled = filas.length === 1
  })
}

async function seleccionarUnidad(unidad: UnidadDidactica): Promise<void> {
  unidadActual = unidad
  elemento('#unidad-seleccionada').textContent =
    `Grado ${unidad.grado}${unidad.grupo} — ${unidad.nombreArchivo}`
  const respuesta = await listarPlaneaciones(unidad.id)
  const lista = elemento<HTMLElement>('#lista-planeaciones')
  lista.replaceChildren(
    ...respuesta.datos.map((dato) => {
      const tarjeta = document.createElement('article')
      tarjeta.className = 'card border mb-3'
      const cuerpo = document.createElement('div')
      cuerpo.className = 'card-body'
      const titulo = document.createElement('h4')
      titulo.className = 'h6'
      titulo.textContent = `Clase ${dato.secuencia}: ${dato.referenteConceptual}`
      const resumen = document.createElement('p')
      resumen.className = 'texto-con-saltos mb-2'
      resumen.textContent = dato.momentoCentral
      const encabezado = document.createElement('div')
      encabezado.className = 'd-flex justify-content-between align-items-start gap-2'
      encabezado.append(titulo)
      const ver = document.createElement('button')
      ver.type = 'button'
      ver.className = 'btn btn-sm btn-outline-secondary me-2'
      ver.textContent = 'Ver detalle'
      ver.addEventListener('click', () => {
        const contenido = elemento<HTMLElement>('#contenido-ver-planeacion')
        const campos = {
          Tema: dato.tema,
          'Referente conceptual': dato.referenteConceptual,
          'Momento inicial': dato.momentoInicial,
          'Momento central': dato.momentoCentral,
          'Momento final': dato.momentoFinal,
          Metodología: dato.metodologia,
          Evaluación: dato.evaluacion,
          Recursos: dato.recursos,
          Bibliografía: dato.bibliografia,
          Observaciones: dato.observaciones,
        }
        contenido.replaceChildren(
          ...Object.entries(campos).map(([tituloCampo, valor]) => {
            const bloque = document.createElement('div')
            bloque.className = 'mb-3'
            const encabezado = document.createElement('h3')
            encabezado.className = 'h6 text-primary'
            encabezado.textContent = tituloCampo
            const texto = document.createElement('p')
            texto.className = 'texto-con-saltos mb-0'
            texto.textContent = valor || 'Sin información'
            bloque.append(encabezado, texto)
            return bloque
          }),
        )
        const tituloCompetencias = document.createElement('h3')
        tituloCompetencias.className = 'h6 text-primary'
        tituloCompetencias.textContent = 'Competencias'
        const tabla = document.createElement('table')
        tabla.className = 'table table-sm table-bordered'
        tabla.innerHTML =
          '<thead><tr><th>Competencia</th><th>Componente</th><th>Indicador de desempeño</th></tr></thead>'
        const cuerpo = document.createElement('tbody')
        dato.competencias.forEach((competencia) => {
          const fila = document.createElement('tr')
          for (const valor of [
            competencia.competencia,
            competencia.componente,
            competencia.indicadorDesempeno,
          ]) {
            const celda = document.createElement('td')
            celda.textContent = valor
            fila.append(celda)
          }
          cuerpo.append(fila)
        })
        tabla.append(cuerpo)
        contenido.append(tituloCompetencias, tabla)
        modalVerPlaneacion.show()
      })
      encabezado.append(ver)
      cuerpo.append(encabezado, resumen)
      if (puedeEditar()) {
        const editar = document.createElement('button')
        editar.type = 'button'
        editar.className = 'btn btn-sm btn-outline-primary'
        editar.textContent = 'Editar planeación'
        editar.addEventListener('click', () => {
          cargarFormulario(dato)
          elemento('#formulario-planeacion').scrollIntoView({ behavior: 'smooth' })
        })
        cuerpo.append(editar)
      }
      tarjeta.append(cuerpo)
      return tarjeta
    }),
  )
  elemento('#detalle-planeaciones').classList.remove('d-none')
  elemento('#formulario-planeacion').classList.toggle('d-none', !puedeEditar())
  cargarFormulario()
}

async function cargarUnidades(): Promise<void> {
  if (!expediente) return
  unidades = (await listarUnidades(expediente.id)).datos
  const cuerpo = elemento<HTMLTableSectionElement>('#tabla-unidades')
  cuerpo.replaceChildren(
    ...unidades.map((unidad) => {
      const fila = document.createElement('tr')
      for (const valor of [
        `${unidad.grado}${unidad.grupo}`,
        unidad.nombreArchivo,
        String(unidad.cantidadPlaneaciones),
        new Date(unidad.creadoEn).toLocaleDateString('es-CO'),
      ]) {
        const celda = document.createElement('td')
        celda.textContent = valor
        fila.append(celda)
      }
      const acciones = document.createElement('td')
      const archivo = document.createElement('a')
      archivo.className = 'btn btn-sm btn-outline-secondary me-1'
      archivo.href = `/api/v1/unidades/${unidad.id}/archivo`
      archivo.target = '_blank'
      archivo.rel = 'noopener'
      archivo.textContent = 'Ver PDF'
      const clases = document.createElement('button')
      clases.type = 'button'
      clases.className = 'btn btn-sm btn-outline-primary'
      clases.textContent = 'Planeaciones'
      clases.addEventListener('click', () => {
        void seleccionarUnidad(unidad).catch((error: unknown) =>
          informar(textoError(error), 'danger'),
        )
      })
      acciones.append(archivo, clases)
      if (puedeEditar()) {
        const editar = document.createElement('button')
        editar.type = 'button'
        editar.className = 'btn btn-sm btn-outline-primary ms-1'
        editar.textContent = 'Cambiar grado/grupo'
        editar.addEventListener('click', () => {
          unidadEditada = unidad
          elemento<HTMLSelectElement>('#grado-editar-unidad').value = String(unidad.grado)
          elemento<HTMLSelectElement>('#grupo-editar-unidad').value = unidad.grupo
          modalUnidad.show()
        })
        acciones.append(editar)
      }
      fila.append(acciones)
      return fila
    }),
  )
  elemento('#vacio-unidades').classList.toggle('d-none', unidades.length > 0)
}

export async function mostrarPlaneacion(nuevo: Expediente, nuevosRoles: string[]): Promise<void> {
  expediente = nuevo
  roles = nuevosRoles
  unidadActual = null
  elemento('#panel-planeacion').classList.remove('d-none')
  elemento('#formulario-unidad').classList.toggle('d-none', !puedeEditar())
  elemento('#detalle-planeaciones').classList.add('d-none')
  await cargarUnidades()
  elemento('#panel-planeacion').scrollIntoView({ behavior: 'smooth', block: 'start' })
}

elemento<HTMLFormElement>('#formulario-unidad').addEventListener('submit', (evento) => {
  evento.preventDefault()
  const formulario = evento.currentTarget as HTMLFormElement
  formulario.classList.add('was-validated')
  const archivo = elemento<HTMLInputElement>('#archivo-unidad').files?.[0]
  if (!formulario.checkValidity() || !archivo || !expediente) return
  mostrarCarga('Verificando y subiendo la unidad didáctica…')
  void crearUnidad(
    expediente.id,
    archivo,
    Number(elemento<HTMLSelectElement>('#grado-unidad').value),
    elemento<HTMLSelectElement>('#grupo-unidad').value,
  )
    .then(() => {
      formulario.reset()
      formulario.classList.remove('was-validated')
      return cargarUnidades()
    })
    .then(() => informar('Unidad didáctica verificada y cargada.'))
    .catch((error: unknown) => informar(textoError(error), 'danger'))
    .finally(cerrarCarga)
})

elemento<HTMLFormElement>('#formulario-planeacion').addEventListener('submit', (evento) => {
  evento.preventDefault()
  const formulario = evento.currentTarget as HTMLFormElement
  formulario.classList.add('was-validated')
  if (!formulario.checkValidity() || !unidadActual) return
  const operacion = planeacionEditada
    ? actualizarPlaneacion(planeacionEditada, datosFormulario())
    : crearPlaneacion(unidadActual.id, datosFormulario())
  void operacion
    .then(() => seleccionarUnidad(unidadActual!))
    .then(() => cargarUnidades())
    .then(() => informar('Planeación guardada correctamente.'))
    .catch((error: unknown) => informar(textoError(error), 'danger'))
})
elemento<HTMLButtonElement>('#agregar-competencia-planeacion').addEventListener('click', () => {
  const datos = filasCompetencias()
  datos.push({ competencia: '', componente: '', indicadorDesempeno: '' })
  renderizarFilasCompetencias(datos)
})
elemento<HTMLButtonElement>('#cancelar-edicion-planeacion').addEventListener('click', () => {
  cargarFormulario()
})
elemento<HTMLFormElement>('#formulario-editar-unidad').addEventListener('submit', (evento) => {
  evento.preventDefault()
  const formulario = evento.currentTarget as HTMLFormElement
  formulario.classList.add('was-validated')
  if (!formulario.checkValidity() || !unidadEditada) return
  void actualizarUnidad(
    unidadEditada,
    Number(elemento<HTMLSelectElement>('#grado-editar-unidad').value),
    elemento<HTMLSelectElement>('#grupo-editar-unidad').value,
  )
    .then(() => {
      modalUnidad.hide()
      formulario.classList.remove('was-validated')
      unidadEditada = null
      return cargarUnidades()
    })
    .then(() => informar('Grado y grupo actualizados correctamente.'))
    .catch((error: unknown) => informar(textoError(error), 'danger'))
})
